import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { ROUTE_ACTIONS, type RouteAction } from '@/app/router/paths'
import type { IUser } from '@/features/users/models/user'
import type { IWorkflow } from '@/features/workflows/models/workflow.schema'
import type { EnumCurrency } from '@/shared/catalogs/currencies'
import { STEPS_NEGOTIATIONS } from '../consts/routes'
import { getNegotiationViewSteps } from '../domain/client-app-eligibility'
import type { IDetailsNegotiationsStore } from '../models/details-negotiation'
import type { IExtendedDueDiligenceNegotiation } from '../models/extended-due-diligence-negotiation'
import type {
  CurrentStateNegotiation,
  INegotiationCreateStore,
  INegotiationEditStore,
  INegotiationOwners,
  INegotiationViewStore
} from '../models/negotiations'
import type { IPaymentAgreementStore } from '../models/payment-agreement'
import type { IProductionOrderLevels } from '../models/production-order'
import type { IReferralNegotiation } from '../models/referral-negotiation'

export const NAME_STORE_NEGOTIATIONS = 'negotiation-store'

interface StoreInterface {
  // CONFIG
  currentState: CurrentStateNegotiation | null
  setCurrentState: (data: CurrentStateNegotiation | null) => void
  workflow: IWorkflow | null
  setWorkflow: (data: IWorkflow | null) => void
  createdAt: string | null
  setCreatedAt: (data: string) => void
  createdBy: IUser | null
  setCreatedBy: (data: IUser | null) => void
  updatedAt: string | null
  setUpdatedAt: (data: string | null) => void
  updatedBy: IUser | null
  setUpdatedBy: (data: IUser | null) => void
  id: string | null
  setId: (data: string) => void
  /**
   * El `neg_` de la negociación, que NO es `id`: ese guarda el `_id` de Mongo, que es
   * lo único que aceptan los endpoints v1 con los que trabaja toda esta pantalla.
   * Los dos conviven mientras dure el strangler — la v2 no acepta el `_id` y la v1 no
   * conoce el `neg_`, así que quedarse con uno solo rompería la mitad del expediente.
   */
  publicId: string | null
  setPublicId: (data: string | null) => void
  isRescindOrEnd: boolean
  setIsRescindOrEnd: (data: boolean) => void
  isInactive: boolean
  setIsInactive: (data: boolean) => void
  code: number | null
  setCode: (data: number) => void
  resetStoreNegotiation: () => void
  // NEGOTIATION
  advisor: string | IUser | null
  setAdvisor: (data: string | IUser | null) => void
  negotiation:
    | INegotiationCreateStore
    | INegotiationEditStore
    | INegotiationViewStore
    | Partial<INegotiationCreateStore>
    | Partial<INegotiationViewStore>
    | null
  setNegotiation: (
    data:
      | INegotiationCreateStore
      | INegotiationEditStore
      | INegotiationViewStore
      | Partial<INegotiationCreateStore>
      | Partial<INegotiationViewStore>
  ) => void
  owners: INegotiationOwners[]
  setOwners: (data: INegotiationOwners[]) => void
  currency: EnumCurrency | null | undefined
  setCurrency: (data: EnumCurrency | null | undefined) => void
  // FORM DETAILS NEGOTIATIONS
  detailsNegotiations: IDetailsNegotiationsStore | null
  setDetailsNegotiations: (data: IDetailsNegotiationsStore | null) => void
  // FORM PAYMENT AGREEMENT
  paymentAgreement: IPaymentAgreementStore | null
  setPaymentAgreement: (data: IPaymentAgreementStore | null) => void
  // FORM EXTENDED DUE DILIGENCE
  extendedDueDiligence: IExtendedDueDiligenceNegotiation | null
  setExtendedDueDiligence: (data: IExtendedDueDiligenceNegotiation | null) => void
  // FORM REFERRAL PROGRAM
  referralNegotiation: IReferralNegotiation | null
  setReferralNegotiation: (data: IReferralNegotiation | null) => void
  // FORM PRODUCTION ORDER
  productionOrder: IProductionOrderLevels[]
  setProductionOrder: (data: IProductionOrderLevels[]) => void
}

export const useNegotiationsStore = create<StoreInterface>()(
  persist(
    (set, get) => ({
      // CONFIG
      currentState: null,
      setCurrentState: (data: CurrentStateNegotiation | null) => {
        set({ currentState: data })
      },
      workflow: null,
      setWorkflow: (data: IWorkflow | null) => {
        set({ workflow: data })
      },
      createdBy: null,
      setCreatedBy: (data: IUser | null) => {
        set({ createdBy: data })
      },
      updatedBy: null,
      setUpdatedBy: (data: IUser | null) => {
        set({ updatedBy: data })
      },
      id: null,
      setId: (data: string) => {
        set({ id: data })
      },
      publicId: null,
      setPublicId: (data: string | null) => {
        set({ publicId: data })
      },
      createdAt: null,
      setCreatedAt: (data: string) => {
        set({ createdAt: data })
      },
      updatedAt: null,
      setUpdatedAt: (data: string | null) => {
        set({ updatedAt: data })
      },
      code: null,
      setCode: (data: number) => {
        set({ code: data })
      },
      isRescindOrEnd: false,
      setIsRescindOrEnd: (data: boolean) => {
        set({ isRescindOrEnd: data })
      },
      isInactive: false,
      setIsInactive: (data: boolean) => {
        set({ isInactive: data })
      },
      // NEGOTIATION
      negotiation: null,
      setNegotiation: (
        data:
          | INegotiationCreateStore
          | INegotiationEditStore
          | INegotiationViewStore
          | Partial<INegotiationCreateStore>
          | Partial<INegotiationViewStore>
      ) => {
        set({ negotiation: data })
      },
      advisor: null,
      setAdvisor: (data: string | IUser | null) => {
        set({ advisor: data })
      },
      owners: [],
      setOwners: (data) => {
        set({ owners: data })
      },
      currency: null,
      setCurrency: (data: EnumCurrency | null | undefined) => {
        set({ currency: data })
      },
      detailsNegotiations: null,
      setDetailsNegotiations: (data: IDetailsNegotiationsStore | null) => {
        set({ detailsNegotiations: data })
      },
      paymentAgreement: null,
      setPaymentAgreement: (data: IPaymentAgreementStore | null) => {
        set({ paymentAgreement: data })
      },
      extendedDueDiligence: null,
      setExtendedDueDiligence: (data: IExtendedDueDiligenceNegotiation | null) => {
        set({ extendedDueDiligence: data })
      },
      referralNegotiation: null,
      setReferralNegotiation: (data: IReferralNegotiation | null) => {
        set({ referralNegotiation: data })
      },
      productionOrder: [],
      setProductionOrder: (data: IProductionOrderLevels[]) => {
        set({ productionOrder: data })
      },
      resetStoreNegotiation: () => {
        set({
          // CONFIG
          currentState: null,
          workflow: null,
          createdAt: null,
          updatedAt: null,
          id: null,
          publicId: null,
          code: null,
          isRescindOrEnd: false,
          isInactive: false,
          currency: null,
          createdBy: null,
          updatedBy: null,
          // NEGOTIATION
          negotiation: null,
          advisor: null,
          owners: [],
          detailsNegotiations: null,
          paymentAgreement: null,
          productionOrder: [],
          extendedDueDiligence: null,
          referralNegotiation: null
        })
      }
    }),
    { name: NAME_STORE_NEGOTIATIONS }
  )
)

interface SubmitStoreInterface {
  formRef: React.RefObject<HTMLFormElement> | null
  setFormRef: (formRef: React.RefObject<HTMLFormElement>) => void
  resetFormRef: () => void
  isValidForm: boolean
  setIsValidForm: (isValidForm: boolean) => void
  step: number
  setStep: (step: number) => void
  nextStep: () => void
  prevStep: () => void
  resetStoreSubmit: () => void
  action: RouteAction | null
  setAction: (data: RouteAction) => void
}

export const useSubmitNegotiationStore = create<SubmitStoreInterface>()((set, get) => ({
  action: null,
  setAction: (data: RouteAction) => {
    set({ action: data })
  },
  formRef: null,
  setFormRef: (formRef: React.RefObject<HTMLFormElement>) => {
    set({ formRef })
  },
  resetFormRef: () => {
    set({ formRef: null })
  },
  isValidForm: false,
  setIsValidForm: (isValidForm: boolean) => {
    set({ isValidForm })
  },
  step: 1,
  setStep: (step: number) => {
    set({ step })
  },
  nextStep: () => {
    const step = get().step
    const action = get().action
    if (action === ROUTE_ACTIONS.view) {
      const { currentState, publicId } = useNegotiationsStore.getState()
      if (step > getNegotiationViewSteps({ currentState, publicId, action }).length) return
    }
    if (step > STEPS_NEGOTIATIONS.length) return
    set({ step: get().step + 1 })
  },
  prevStep: () => {
    const step = get().step
    if (step === 1) return
    set({ step: get().step - 1 })
  },
  resetStoreSubmit: () => {
    set({
      formRef: null,
      action: null,
      isValidForm: false,
      step: 1
    })
  }
}))
